export const name = 'rnRecordFactory';

/**
 * @ngdoc service
 * @name rhino.core.record.service:rnRecordFactory
 * @description Creates a common implementation of the Active Record pattern by Matrin Fowler
 * using Genesis JSON+HAL (Hypertext Application Language) links.
 * Data for objects are merge directly from server responses into a new record by using the `create` method.
 *
 * Any data and objects used internally by the UI and not meant to be sent back to the server
 * is stored on the `internal` child object.
 *
 * A number of `canXXXX` functions exist to check if an operation is allowed.
 * These functions check if HAL links exist for the requested operation.
 * If an operation is not allowed, trying to invoke that operation will throw an error.
 *    Side discussion: should it return a failed promise instead?
 *    No, it should fail spectacularly if the caller didn't check the corresponding `can` function.
 *    Yes, since these functions already return promises.  But, then we have to create a record with the `$q` dependency as well.
 *
 * Each operation function (`save`, `create`, `delete`, `update`) returns a promise
 * that is resolved after the server responds to the request.
 * The `save` operation is a shortcut to `create` if the record is new or `update` if the record already exists.
 * The record is determined to exist if it has an `id` attribute (or the attribute specified in `internal.idName`).
 */
export default function rnRecordFactory(rnHttp) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  // (Class definition must come before factory is returned because class definitions are not hoisted.)
  class RhinoRecord {
    constructor(data) {
      // Store internals on a separate object.
      // Could also create an "addInternalProperty" method that
      // uses Object.defineProperty to create an non-enumerable property on the object
      this.internal = {};
      this.internal.idName = 'id';

      // Bind server returned data to do this object
      angular.extend(this, data);
    }

    /**
     * Creates a new, independent record.
     * @returns {Object}
     */
    clone() {
      return angular.copy(this);
    }

    throwInvalidOp(op) {
      throw {
        error: `Invalid operation "${op}" for record`,
        record: this
      };
    }

    /**
     * @ngdoc function
     * @name getData
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @description Get just the data portion of this record (typically before sending to the server)
     * @returns {object} just the data portion of this record without any internal data or objects
     */
    getData() {
      // remove class variables
      return _.omit(this, ['internal']);
    }

    /**
     * @ngdoc function
     * @name save
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @description The `save` operation is a shortcut to `create` if the record is new or `update` if the record already exists.
     * @returns {IHttpPromise<T>} a server request promise
     * @throws unless `canSave`
     */
    save() {
      if(!this.canSave()) {
        this.throwInvalidOp('save');
      }

      if(this.isNew()) {
        return this.create();
      }
      return this.update();
    }

    /**
     * @ngdoc function
     * @name create
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @description Requests the server creates the record.
     * @returns {IHttpPromise<T>} a server request promise
     * @throws unless `canUpdate`
     */
    create() {
      if(!this.canCreate()) {
        this.throwInvalidOp('create');
      }
      let link = this.getLink('create');

      if(!link.op) {
        console.warn('HAL link missing HTTP method property "op".  Assuming op "POST" for method create. Record: ');
        console.dir(this);
        link = _.cloneDeep(link);
        link.op = 'POST';
      }

      return this.makeHalRequest(link, this.getData());
    }

    /**
     * @ngdoc function
     * @name destroy
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @description Requests the server deletes the record.
     * @returns {IHttpPromise<T>} a server request promise
     * @throws unless `canDestroy`
     */
    destroy() {
      if(!this.canDestroy()) {
        this.throwInvalidOp('destroy');
      }

      let link = this.getLink('delete');
      if(!link.op) {
        console.warn('HAL link missing HTTP method property "op".  Assuming op "DELETE" for method destroy. Record: ');
        console.dir(this);
        link = _.cloneDeep(link);
        link.op = 'DELETE';
      }

      return this.makeHalRequest(link);
    }

    /**
     * @ngdoc function
     * @name update
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @description Requests the server updates the record.
     * @returns {IHttpPromise<T>} a server request promise
     * @throws unless `canUpdate`
     */
    update() {
      if(!this.canUpdate()) {
        this.throwInvalidOp('update');
      }

      let link;
      if(this.hasLink('update')) {
        link = this.getLink('update');
      } else if(this.hasLink('edit')) {
        console.warn('HAL link is named "edit" for an update operation. Change API to be "update" instead. Record: ');
        console.dir(this);
        link = this.getLink('edit');
      }


      if(!link.op) {
        console.warn('HAL link missing HTTP method property "op".  Assuming op "PUT" for method update. Record: ');
        console.dir(this);
        link = _.cloneDeep(link);
        link.op = 'PUT';
      }

      return this.makeHalRequest(link, this.getData());
    }

    /**
     * Reloads the record from the server.  This instance will be updated once the promise is resolved,
     * and the instance will be returned in the resolved promise.
     *
     * @returns {IPromise<HalActiveRecord>} a promise of the updated record.
     * @throws unless `canReload`
     */
    reload() {
      // TODO: Implement
    }

    /**
     * @returns {boolean} `true` if the data has been modified since it was requested, `false` otherwise
     */
    isDirty() {
      // TODO: Implement
      // How do we implement this?  Some ideas,
      //   1. We could try something like an agular $watch or Object.watch that would inform us of changes when they're made.
      //      Using $watch() too much has performance implications.
      //   2. If we're not worried about memory, we could create an initial copy and store it in internals.  Seems heavy but should work.
      //   3. Somehow use ES5 getters ('get') and setters ('set') or ES6 computed properties with a setter?
    }

    validate() {
      // TODO: Implement
    }

    isValid() {
      // TODO: Implement
    }

    /**
     * @ngdoc function
     * @name getLink
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @param {string} linkName name of the link to find
     * @returns {Object} a link object, supporting the HAL conventional link property name '_links' as well as 'links'
     */
    getLink(linkName) {
      let links;
      if(_.has(this, '_links')) {
        links = this._links;
      } else if (_.has(this, 'links')) {
        console.warn('Record has unconventional property "links".  APIs should provide "_links" instead. Record: ');
        console.dir(this);
        links = this.links;
      }

      if(links) {
        return links[linkName];
      }
      return undefined;
    }

    /**
     * @ngdoc function
     * @name hasLink
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @param {string} linkName name of the link to check for
     * @returns {boolean} `true` if the record has a specified link
     */
    hasLink(linkName) {
      return this.getLink(linkName) !== undefined;
    }

    /**
     * @ngdoc function
     * @name canSave
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @returns {boolean} `true` if the record can be saved
     */
    canSave() {
      if(this.isNew()) {
        return this.canCreate();
      }
      return this.canUpdate();
    }

    /**
     * @ngdoc function
     * @name canCreate
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @returns {boolean} `true` if the record can be created
     */
    canCreate() {
      return this.hasLink('create');
    }

    /**
     * @ngdoc function
     * @name canDestroy
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @returns {boolean} `true` if the record can be destroyed
     */
    canDestroy() {
      return this.hasLink('delete');
    }

    /**
     * @ngdoc function
     * @name canUpdate
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @returns {boolean} `true` if the record can be updated
     */
    canUpdate() {
      return this.hasLink('update') || this.hasLink('edit');
    }

    /**
     * @ngdoc function
     * @name canReload
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @returns {boolean} `true` if the record can be reloaded
     */
    canReload() {
      return this.hasLink('self');
    }

    /**
     * @returns {boolean} returns `true` if the record has an `id` attribute (or the attribute specified in `internal.idName`), false `otherwise`.
     */
    isNew() {
      return !_.has(this, this.internal.idName);
    }

    /**
     * @ngdoc function
     * @name setRelativeService
     * @methodOf rhino.core.record.service:rnRecordFactory
     * @description Sets the relative service for HAL links to be used against.  Necessary because HAL links are currently root-relative instead of absolute.
     *
     * @param {string} serviceName name of the serivce to make HAL requests relative to.
     */
    setRelativeService(serviceName) {
      this.internal.relativeService = serviceName;
    }

    makeHalRequest(link, data) {
      return rnHttp.halRequest(link, data, null, this.internal.relativeService);
    }
  } // RhinoRecord

  let factory = {
    create: createRecord
  };
  return factory;

  /////////////////

  /**
   * @ngdoc function
   * @name create
   * @methodOf rhino.core.record.service:rnRecordFactory
   * @description Creates an active record with data bound to it.
   *
   * @param {object} data a data object returned from the server
   * @returns {RhinoRecord} a new record
   */
  function createRecord(data) {
    return new RhinoRecord(data);
  }
} // rnRecordFactory



// WEBPACK FOOTER //
// ../~/@printos/rhino/src/core/record/record.factory.js